import { FC } from 'react';

import {
  AnalyticsCategory,
  AnalyticsEvent,
  RoutePath,
} from '@common/constants';
import { useRoute, Redirect } from '@core/navigation';
import { TrackPageView, useAnalytics } from '@wearehelpful/ui-stats';
import { useTranslation } from '@wearehelpful/ui-localization';
import { ContentfulImage } from '@helpful/network';
import {
  Content,
  Typography,
  Form,
  Skeleton,
  InfiniteScroll,
  Footer,
} from '@wearehelpful/ui-kit';
import { usePayors } from '@common/hooks';

import { PayorForm, RouteSearch } from './Insurance.types';

export const Insurance: FC = () => {
  const [
    {
      search: { zip, fips },
    },
    { push },
  ] = useRoute<{}, RouteSearch>();
  const { t } = useTranslation();
  const { track } = useAnalytics();
  const { isLoading, hasNextPage, payors, fetchNextPage } = usePayors({
    filter: { zip, fips },
  });

  const handleOnSubmit = ({ payor }: PayorForm) => {
    const { name } = payors.find(({ id }) => id === payor);
    track({
      id: AnalyticsEvent.SELECT_PAYOR,
      properties: {
        payor: name,
        zip,
        fips,
      },
    }).finally(() => {
      push({
        path: RoutePath.AUTH_SIGN_UP_PLAN,
        search: { id: payor, zip, fips },
      });
    });
  };

  if (!zip || !fips) {
    return <Redirect path={RoutePath.AUTH_SIGN_UP} />;
  }

  return (
    <TrackPageView
      category={AnalyticsCategory.AUTH}
      name={AnalyticsCategory.PAYOR}
      properties={{ zip, fips }}
    >
      <Form<PayorForm> onSubmit={handleOnSubmit}>
        <Content>
          <Content.Progress value={3 / 5} />
          <Content.Subheader>
            <Typography variant="h2">
              {t('subheadings.signUpPayor.title')}
            </Typography>
          </Content.Subheader>
          <Content.Fixed>
            <Skeleton variant="list" loading={isLoading}>
              <InfiniteScroll disabled={!hasNextPage} onScroll={fetchNextPage}>
                <Form.RadioGroup name="payor" validation={{ required: true }}>
                  {payors.map(({ id, name, logo }) => {
                    return (
                      <Form.RadioGroup.Item
                        key={id}
                        value={id}
                        inset={!!logo ? 'sm' : 'md'}
                      >
                        {!!logo && (
                          <>
                            <ContentfulImage url={logo} height={36} />
                            &nbsp;&nbsp;&nbsp;
                          </>
                        )}
                        {name}
                      </Form.RadioGroup.Item>
                    );
                  })}
                </Form.RadioGroup>
              </InfiniteScroll>
            </Skeleton>
          </Content.Fixed>
        </Content>
        <Footer>
          <Form.Submit checkValidity disabled={isLoading}>
            {t('actions.next')}
          </Form.Submit>
        </Footer>
      </Form>
    </TrackPageView>
  );
};
